import { apiClient } from '@/shared/api/http-client';
import { createApiPath } from '@/shared/api/endpoints';
import { Pageable, PageResponse } from '@/entities/user/api/user.api';
import { compressImage } from '@/shared/utils/compressImage';

export type FaqCategory =
  | 'USER'
  | 'PRESCRIPTION'
  | 'SETTLEMENT'
  | 'DRUG_MATCHING'
  | 'FILTERING';

export interface FaqItem {
  faqId: number;
  id?: number;
  category: FaqCategory;
  question: string;
  answer: string;
}

export interface FaqUpsertRequest {
  category: FaqCategory;
  question: string;
  answer: string;
}

export type AnnouncementCategory =
  | 'SERVICE_UPDATE'
  | 'POLICY'
  | 'EVENT'
  | 'MAINTENANCE'
  | 'SCHEDULE_NOTICE'
  | string;

export type PharmaSelectionType = 'SELECTED' | 'ALL' | 'OTHERS';

export interface AnnouncementScheduleDeadline {
  deadlineDate: string;
  pharmaSelectionType: PharmaSelectionType;
  pharmaCompanyIds?: number[];
  pharmaCompanies?: Array<{
    pharmaCompanyId: number;
    pharmaCompanyName?: string;
  }>;
}

export interface AnnouncementSchedule {
  yearMonth: string;
  topContent?: string | null;
  bottomContent?: string | null;
  deadlines: AnnouncementScheduleDeadline[];
}

export interface AnnouncementListItem {
  id: number;
  title: string;
  category: AnnouncementCategory;
  isPublished: boolean;
  publishedStartAt: string;
  publishedEndAt: string;
  createdAt: string;
  createdBy?: string;
  viewCount?: number;
  landingUrl?: string;
  status?: string;
}

export interface AnnouncementDetail extends AnnouncementListItem {
  content: string | null;
  publishStartAt?: string;
  publishEndAt?: string;
  thumbnailUrl?: string;
  pharmaCompanyIds?: number[];
  isMainPublished?: boolean;
  /** 마지막 저장에서 알림톡 발송을 요청했는지 */
  isAlimtalkSend?: boolean | null;
  schedule?: AnnouncementSchedule;
  registrationYearMonth?: string;
  scheduleTopContent?: string;
  scheduleBottomContent?: string;
  registrationScheduleItems?: AnnouncementScheduleDeadline[];
  registrationDeadlines?: string[];
}

export interface AnnouncementUpsertRequest {
  title: string;
  category: AnnouncementCategory;
  content: string | null;
  isPublished: boolean;
  isMainPublished?: boolean;
  /**
   * 알림톡 발송 여부. 생략하면 서버가 true 로 간주하므로
   * 프론트는 항상 boolean 을 명시한다.
   */
  isAlimtalkSend: boolean;
  publishStartAt: string;
  publishEndAt: string;
  thumbnailUrl?: string;
  landingUrl?: string;
  pharmaCompanyIds?: number[];
  schedule?: AnnouncementSchedule;
}

export interface AnnouncementSearchRequest {
  title?: string;
  category?: string;
  isPublished?: boolean;
  publicationStatusList?: string[];
}

export const boardService = {
  getFaqs: async (): Promise<FaqItem[]> => {
    const response = await apiClient.get(createApiPath.board.faqs());
    return response.data;
  },

  createFaq: async (data: FaqUpsertRequest) => {
    const response = await apiClient.post(createApiPath.board.faqs(), data);
    return response.data;
  },

  updateFaq: async (faqId: number, data: FaqUpsertRequest) => {
    const response = await apiClient.put(createApiPath.board.faq(faqId), data);
    return response.data;
  },

  deleteFaq: async (faqId: number) => {
    await apiClient.delete(createApiPath.board.faq(faqId));
  },

  getAnnouncements: async (
    search: AnnouncementSearchRequest,
    pageable: Pageable,
  ): Promise<PageResponse<AnnouncementListItem>> => {
    const params: Record<string, string | number | boolean | string[]> = {
      page: pageable.page,
      size: pageable.size,
    };
    if (search.title) params.title = search.title;
    if (search.category) params.category = search.category;
    if (search.isPublished != null) params.isPublished = search.isPublished;
    if (search.publicationStatusList?.length) {
      params.publicationStatusList = search.publicationStatusList;
    }

    const response = await apiClient.get(createApiPath.board.notices(), {
      params,
      paramsSerializer: {
        indexes: null,
      },
    });
    return response.data;
  },

  getAnnouncement: async (id: number): Promise<AnnouncementDetail> => {
    const response = await apiClient.get(createApiPath.board.notice(id));
    return response.data;
  },

  createAnnouncement: async (data: AnnouncementUpsertRequest) => {
    const response = await apiClient.post(createApiPath.board.notices(), data);
    return response.data;
  },

  updateAnnouncement: async (id: number, data: AnnouncementUpsertRequest) => {
    const response = await apiClient.put(createApiPath.board.notice(id), data);
    return response.data;
  },

  uploadAnnouncementImage: async (file: File): Promise<string> => {
    // 레거시: 업로드 전 maxWidth 1200 / quality 0.8 압축
    const compressed = await compressImage(file);
    const formData = new FormData();
    formData.append('file', compressed);
    const response = await apiClient.post(
      createApiPath.board.noticeImages(),
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data.imageUrl as string;
  },
};
